// ===================================================================
// KAIXO Direct — Traducción cara a cara push-to-talk
// ===================================================================
// Pantalla fullscreen. Dos personas comparten un solo móvil para
// hablar en idiomas distintos sin que el otro tenga la app.
//
// Flujo:
//   1) Setup inicial: elegir idioma A (mío) y idioma B (del otro)
//   2) Mantener pulsado el botón → grabar audio
//   3) Al soltar → enviar al backend que detecta idioma + traduce + TTS
//   4) Reproducir audio en idioma opuesto + mostrar transcripciones
//   5) Repetir N veces (cualquiera puede hablar varias veces seguidas)
// ===================================================================

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import api from '../../services/api';
import t from '../../i18n/useTranslation';
import QuickRechargeModal from '../QuickRecharge/QuickRechargeModal';
import './KaixoDirect.css';

const STATE_SETUP    = 'setup';
const STATE_IDLE     = 'idle';
const STATE_RECORDING= 'recording';
const STATE_SENDING  = 'sending';
const STATE_PLAYING  = 'playing';

export default function KaixoDirect({ onClose, currentUser }) {
    const [phase, setPhase]     = useState(STATE_SETUP);
    const [langs, setLangs]     = useState([]);   // catálogo desde backend
    const [langA, setLangA]     = useState('es'); // mi idioma
    const [langB, setLangB]     = useState('en'); // idioma del otro
    // Modo manos libres (B7 2026-05-06): si activo, no se selecciona langB.
    // El backend detecta automáticamente el idioma del audio entrante y
    // traduce a inglés (default) si es langA, o a langA si es otro idioma.
    // Caso de uso: recepcionista pone móvil en mostrador, ambos hablan sin
    // que nadie pulse selectores cada turno.
    const [autoMode, setAutoMode] = useState(false);
    const [error, setError]     = useState('');
    const [turns, setTurns]     = useState([]);   // historial visible
    const [lastTimings, setLastTimings] = useState(null);
    // Modal de recarga rápida cuando el backend devuelve 402 (saldo agotado).
    // El usuario ve "Saldo agotado" + 3 packs ($5/$10/$25) sin salir de Direct.
    const [showRecharge, setShowRecharge] = useState(false);
    const [rechargeReason, setRechargeReason] = useState('empty');

    const mediaRecorderRef = useRef(null);
    const chunksRef        = useRef([]);
    const audioElRef       = useRef(null);
    const streamRef        = useRef(null);
    const historyEndRef    = useRef(null);
    // URL actualmente cargada en el <audio>. Necesario para revocarla
    // al sustituirla por otra (evita leak de Blob URLs en replay).
    const currentAudioUrlRef = useRef(null);
    // Cola de reproducción secuencial NDJSON streaming.
    // Cada item = blob URL de oración. El audio playback de la oración
    // N+1 arranca cuando termina N. Permite que se oiga la 1ª oración
    // mientras siguen bajando las traducciones del resto del turno.
    const audioQueueRef    = useRef([]);
    const audioPlayingRef  = useRef(false);

    // Auto-scroll al final cada vez que se añade un turno o aparece "Traduciendo…"
    useEffect(() => {
        historyEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [turns, phase]);

    // Cargar catálogo de idiomas
    useEffect(() => {
        api.get('/direct/languages')
            .then(({ data }) => setLangs(data?.languages || []))
            .catch(() => {
                // Fallback al subset mínimo si el backend no responde
                setLangs([
                    { code: 'es', name: 'Español' },
                    { code: 'en', name: 'English' },
                    { code: 'fr', name: 'Français' },
                    { code: 'de', name: 'Deutsch' },
                    { code: 'it', name: 'Italiano' },
                    { code: 'pt', name: 'Português' },
                    { code: 'ja', name: '日本語' },
                    { code: 'zh', name: '中文' }
                ]);
            });
    }, []);

    // Detectar idioma del navegador para preseleccionar langA
    useEffect(() => {
        const nav = (navigator.language || '').slice(0, 2);
        if (['es','en','fr','de','it','pt','ja','zh','ko','ru','nl','pl','tr','ar'].includes(nav)) {
            setLangA(nav);
            // Si por defecto B es igual que A, cámbialo a un sensato distinto
            setLangB(prev => prev === nav ? (nav === 'es' ? 'en' : 'es') : prev);
        }
    }, []);

    // Limpieza al desmontar
    useEffect(() => {
        return () => {
            try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch {}
            try { mediaRecorderRef.current?.stop(); } catch {}
            // Revocar la URL del audio actual si la hay (evita leak)
            if (currentAudioUrlRef.current) {
                try { URL.revokeObjectURL(currentAudioUrlRef.current); } catch {}
                currentAudioUrlRef.current = null;
            }
            // Revocar URLs pendientes en la cola de reproducción
            if (audioQueueRef.current?.length) {
                audioQueueRef.current.forEach(url => { try { URL.revokeObjectURL(url); } catch {} });
                audioQueueRef.current = [];
            }
        };
    }, []);

    const startRecording = useCallback(async () => {
        if (phase === STATE_RECORDING || phase === STATE_SENDING) return;
        setError('');
        try {
            // OPT3: pedimos sample rate bajo (16kHz suficiente para voz) +
            // canal mono. Reduce ~4x el tamaño del audio enviado, ahorra
            // upload time especialmente en 4G.
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1
                }
            });
            streamRef.current = stream;
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : '';
            // 24 kbps opus = calidad voz humana excelente y archivo 4x más
            // pequeño que el default (~96 kbps).
            const recorderOpts = mimeType
                ? { mimeType, audioBitsPerSecond: 24000 }
                : { audioBitsPerSecond: 24000 };
            const recorder = new MediaRecorder(stream, recorderOpts);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            recorder.start();
            mediaRecorderRef.current = recorder;
            setPhase(STATE_RECORDING);
        } catch (err) {
            setError((t.direct?.errMic || 'No se pudo acceder al micrófono: ') + err.message);
            setPhase(STATE_IDLE);
        }
    }, [phase]);

    const playNextInQueue = useCallback(() => {
        const audioEl = audioElRef.current;
        if (!audioEl) return;
        if (audioQueueRef.current.length === 0) {
            audioPlayingRef.current = false;
            // Solo terminamos en STATE_IDLE si NO hay más oraciones que estén
            // todavía bajando en el stream. Si la respuesta sigue activa, el
            // siguiente audio que llegue volverá a llamar play.
            return;
        }
        const nextUrl = audioQueueRef.current.shift();
        // Revocar URL anterior
        if (currentAudioUrlRef.current && currentAudioUrlRef.current !== nextUrl) {
            try { URL.revokeObjectURL(currentAudioUrlRef.current); } catch {}
        }
        currentAudioUrlRef.current = nextUrl;
        audioEl.src = nextUrl;
        audioPlayingRef.current = true;
        audioEl.play().catch(() => {});
    }, []);

    const enqueueAudio = useCallback((blob) => {
        const url = URL.createObjectURL(blob);
        audioQueueRef.current.push(url);
        if (!audioPlayingRef.current) {
            playNextInQueue();
        }
    }, [playNextInQueue]);

    const stopAndSend = useCallback(async () => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state !== 'recording') return;

        const stopped = new Promise(resolve => { recorder.onstop = resolve; });
        recorder.stop();
        await stopped;
        try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch {}

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        if (blob.size < 500) {
            setPhase(STATE_IDLE);
            setError(t.direct?.errTooShort || 'Audio demasiado corto, mantén pulsado mientras hablas');
            return;
        }

        setPhase(STATE_SENDING);
        try {
            // V2 streaming: backend devuelve NDJSON. Frames:
            //   {type:"meta", detectedLang, targetLang, sourceText}
            //   {type:"audio", idx, text, audioB64}  — uno por oración
            //   {type:"done", targetText, timings}
            // Reproducimos cada audio conforme llega → primer sonido al cliente
            // mucho antes de que toda la traducción termine.
            const fd = new FormData();
            fd.append('audio', blob, 'turn.webm');
            fd.append('langA', langA);
            if (!autoMode) {
                fd.append('langB', langB);
            }

            const baseURL = (api.defaults.baseURL || '').replace(/\/+$/, '');
            const token = localStorage.getItem('kaixo_token') || '';
            const r = await fetch(baseURL + '/direct/turn', {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: fd
            });
            if (!r.ok) {
                let errBody;
                try { errBody = await r.json(); } catch { errBody = { error: `HTTP ${r.status}` }; }
                // 402 = wallet/plan agotado → abrir modal de recarga rápida
                // sin sacar al usuario de Direct. Cuando recarga, vuelve aquí
                // y puede reintentar la frase.
                if (r.status === 402 || errBody?.error === 'wallet_or_plan_required') {
                    setRechargeReason('empty');
                    setShowRecharge(true);
                    setPhase(STATE_IDLE);
                    return;
                }
                const stage = errBody?.stage ? ` (etapa: ${errBody.stage})` : '';
                setError((errBody?.error || `HTTP ${r.status}`) + stage);
                setPhase(STATE_IDLE);
                console.error('[KaixoDirect] turn error:', errBody);
                return;
            }

            const audioEl = audioElRef.current;
            if (!audioEl) { setPhase(STATE_IDLE); return; }

            // Cuando termina cada audio en queue, intenta el siguiente
            audioEl.onended = () => playNextInQueue();

            // Limpiar queue de turnos previos
            audioQueueRef.current.forEach(url => { try { URL.revokeObjectURL(url); } catch {} });
            audioQueueRef.current = [];
            audioPlayingRef.current = false;

            // Parser NDJSON streaming
            const reader = r.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let lineBuf = '';
            let metaInfo = null;
            const sentenceTexts = [];
            const sentenceBlobs = [];
            let timings = {};

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                lineBuf += decoder.decode(value, { stream: true });

                let newlineIdx;
                while ((newlineIdx = lineBuf.indexOf('\n')) !== -1) {
                    const line = lineBuf.slice(0, newlineIdx).trim();
                    lineBuf = lineBuf.slice(newlineIdx + 1);
                    if (!line) continue;
                    let frame;
                    try { frame = JSON.parse(line); } catch (e) {
                        console.warn('[KaixoDirect] frame inválido:', line.slice(0, 80));
                        continue;
                    }

                    if (frame.type === 'meta') {
                        metaInfo = frame;
                        // Cambiar a STATE_PLAYING en cuanto haya meta — ya estamos
                        // recibiendo respuesta. El primer audio sonará pronto.
                        setPhase(STATE_PLAYING);
                    } else if (frame.type === 'audio' && frame.audioB64) {
                        // Decodificar base64 a Blob audio/mpeg
                        const binStr = atob(frame.audioB64);
                        const bytes = new Uint8Array(binStr.length);
                        for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
                        const audioBlob = new Blob([bytes], { type: 'audio/mpeg' });
                        sentenceTexts[frame.idx] = frame.text;
                        sentenceBlobs[frame.idx] = audioBlob;
                        enqueueAudio(audioBlob);
                    } else if (frame.type === 'audio_error') {
                        console.warn('[KaixoDirect] oración falló TTS:', frame.idx, frame.error);
                        sentenceTexts[frame.idx] = frame.text;
                    } else if (frame.type === 'done') {
                        timings = frame.timings || {};
                        if (metaInfo) metaInfo.targetText = frame.targetText;
                    } else if (frame.type === 'error') {
                        setError(frame.error || 'Error');
                        setPhase(STATE_IDLE);
                        return;
                    }
                }
            }

            // Construir un blob "completo" para replay (concatenando todas las oraciones)
            const validBlobs = sentenceBlobs.filter(Boolean);
            const fullAudioBlob = validBlobs.length > 0
                ? new Blob(validBlobs, { type: 'audio/mpeg' })
                : null;

            const turn = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                detectedLang: metaInfo?.detectedLang,
                targetLang: metaInfo?.targetLang,
                sourceText: metaInfo?.sourceText,
                targetText: metaInfo?.targetText || sentenceTexts.filter(Boolean).join(' '),
                audioBlob: fullAudioBlob
            };
            setTurns(prev => [...prev, turn]);
            setLastTimings(timings);

            // Cuando termine el último audio en queue, volver a IDLE
            const checkDoneInterval = setInterval(() => {
                if (!audioPlayingRef.current && audioQueueRef.current.length === 0) {
                    clearInterval(checkDoneInterval);
                    setPhase(STATE_IDLE);
                }
            }, 200);
        } catch (err) {
            const errData = err.response?.data;
            const stage = errData?.stage ? ` (etapa: ${errData.stage})` : '';
            setError((errData?.error || err.message) + stage);
            setPhase(STATE_IDLE);
            console.error('[KaixoDirect] turn error:', errData || err);
        }
    }, [langA, langB, autoMode, enqueueAudio, playNextInQueue]);

    const replayTurn = (turn) => {
        if (!audioElRef.current || !turn.audioBlob) return;
        // Revocar URL anterior antes de crear una nueva
        if (currentAudioUrlRef.current) {
            try { URL.revokeObjectURL(currentAudioUrlRef.current); } catch {}
        }
        const url = URL.createObjectURL(turn.audioBlob);
        currentAudioUrlRef.current = url;
        audioElRef.current.src = url;
        audioElRef.current.play().catch(() => {});
    };

    // swapLangs sin side effect dentro del setState updater (anti-patrón
    // en React). Leemos los valores actuales y aplicamos ambos setters.
    const swapLangs = () => {
        const a = langA;
        const b = langB;
        setLangA(b);
        setLangB(a);
    };

    // ─── Render ──────────────────────────────────────────────
    const langName = (code) => langs.find(l => l.code === code)?.name || code;

    if (phase === STATE_SETUP) {
        return ReactDOM.createPortal(
            <div className="kdir-overlay">
                <div className="kdir-setup">
                    <button className="kdir-close" onClick={onClose} aria-label="cerrar">✕</button>
                    <div className="kdir-setup-head">
                        <Icons.Globe size={28} color="#F5A000" />
                        <h2>KAIXO Direct</h2>
                        <p>{t.direct?.tagline || 'Habla con cualquiera. Cualquier idioma. Sin que descarguen nada.'}</p>
                    </div>

                    <div className="kdir-lang-block">
                        <label>{t.direct?.yourLang || 'Tu idioma'}</label>
                        <select value={langA} onChange={e => setLangA(e.target.value)} className="kdir-select">
                            {langs.map(l => (
                                <option key={l.code} value={l.code} disabled={l.code === langB}>
                                    {l.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button className="kdir-swap-btn" onClick={swapLangs} aria-label={t.direct?.swap || 'intercambiar'} disabled={autoMode}>⇅</button>

                    <div className="kdir-lang-block" style={{ opacity: autoMode ? 0.4 : 1 }}>
                        <label>{t.direct?.otherLang || 'Idioma de la otra persona'}</label>
                        <select
                            value={langB}
                            onChange={e => setLangB(e.target.value)}
                            className="kdir-select"
                            disabled={autoMode}
                        >
                            {langs.map(l => (
                                <option key={l.code} value={l.code} disabled={l.code === langA}>
                                    {l.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Toggle "Modo manos libres" (B7 2026-05-06).
                        Cuando activo: no se selecciona idioma del otro, el sistema lo
                        detecta automáticamente. Útil para recepción hotel: poner móvil
                        en el mostrador, ambos hablan sin pulsar nada. */}
                    <label className="kdir-handsfree-toggle" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={autoMode}
                            onChange={e => setAutoMode(e.target.checked)}
                            style={{ width: 18, height: 18 }}
                        />
                        <span style={{ fontSize: 14 }}>
                            <strong>{t.direct?.handsFree || 'Modo manos libres'}</strong>
                            <br />
                            <span style={{ fontSize: 12, color: '#888' }}>
                                {t.direct?.handsFreeHint || 'No selecciono el otro idioma — el sistema lo detecta automáticamente'}
                            </span>
                        </span>
                    </label>

                    <button
                        className="kdir-start-btn"
                        onClick={() => setPhase(STATE_IDLE)}
                        disabled={!autoMode && langA === langB}
                    >
                        {t.direct?.start || 'Empezar'}
                    </button>

                    <p className="kdir-legal">
                        {t.direct?.legal || 'El audio se envía a nuestros servicios de IA para traducirlo. Se borra tras la respuesta. No usamos tus conversaciones para entrenar modelos.'}
                    </p>
                </div>
            </div>,
            document.body
        );
    }

    return ReactDOM.createPortal(
        <div className="kdir-overlay kdir-active">
            <div className="kdir-main">
                <div className="kdir-topbar">
                    <button className="kdir-close" onClick={onClose} aria-label="cerrar">✕</button>
                    <div className="kdir-pair">
                        <span className="kdir-pair-lang">{langName(langA)}</span>
                        <span className="kdir-pair-arrow">⇄</span>
                        <span className="kdir-pair-lang">{langName(langB)}</span>
                    </div>
                    <button
                        className="kdir-settings-btn"
                        onClick={() => setPhase(STATE_SETUP)}
                        aria-label="cambiar idiomas"
                    >⚙</button>
                </div>

                <div className="kdir-history">
                    {turns.length === 0 && (
                        <div className="kdir-hint">
                            <div className="kdir-hint-icon">🎤</div>
                            <p>Mantén pulsado el botón mientras hablas.</p>
                            <p>Suéltalo y oirás la traducción.</p>
                        </div>
                    )}
                    {turns.map(t => (
                        <div key={t.id}
                             className={`kdir-turn ${t.detectedLang === langA ? 'kdir-turn-mine' : 'kdir-turn-theirs'}`}>
                            <div className="kdir-turn-meta">
                                <span>{langName(t.detectedLang)} → {langName(t.targetLang)}</span>
                                <button className="kdir-replay" onClick={() => replayTurn(t)} aria-label="repetir">
                                    🔊
                                </button>
                            </div>
                            <div className="kdir-turn-source">{t.sourceText}</div>
                            <div className="kdir-turn-target">{t.targetText}</div>
                        </div>
                    ))}
                    <div ref={historyEndRef} />
                </div>

                {error && <div className="kdir-error">{error}</div>}

                <div className="kdir-bottom">
                    <button
                        className={`kdir-talk-btn kdir-talk-${phase}`}
                        onMouseDown={startRecording}
                        onMouseUp={stopAndSend}
                        onMouseLeave={() => phase === STATE_RECORDING && stopAndSend()}
                        onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                        onTouchEnd={(e) => { e.preventDefault(); stopAndSend(); }}
                        disabled={phase === STATE_SENDING || phase === STATE_PLAYING}
                    >
                        {phase === STATE_RECORDING && <span className="kdir-pulse"></span>}
                        <Icons.Microphone size={42} color="white" />
                    </button>
                    <div className="kdir-status">
                        {phase === STATE_IDLE      && 'Mantén pulsado para hablar'}
                        {phase === STATE_RECORDING && '● Grabando — suelta cuando termines'}
                        {phase === STATE_SENDING   && '⏳ Traduciendo…'}
                        {phase === STATE_PLAYING   && '🔊 Reproduciendo'}
                    </div>
                    {lastTimings && phase === STATE_IDLE && (
                        <div className="kdir-timings">
                            Última traducción: {(lastTimings.totalMs / 1000).toFixed(1)}s
                        </div>
                    )}
                </div>

                <audio ref={audioElRef} style={{ display: 'none' }} />
            </div>
            <QuickRechargeModal
                open={showRecharge}
                reason={rechargeReason}
                currentUser={currentUser}
                onClose={() => setShowRecharge(false)}
            />
        </div>,
        document.body
    );
}
